'use client';

import { createClient } from '@/lib/supabase/client';

export interface SupportMessage {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  senderRole: 'student' | 'lecturer' | 'admin';
  message: string;
  isRead: boolean;
  createdAt: string;
}

export interface SupportThread {
  threadId: string;
  studentId: string;
  studentName: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
}

function dbToMessage(row: any): SupportMessage {
  return {
    id: row.id,
    threadId: row.thread_id,
    senderId: row.sender_id,
    senderName: row.sender_name,
    senderRole: row.sender_role as SupportMessage['senderRole'],
    message: row.message,
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

export const supportMessageService = {
  async getByThread(threadId: string): Promise<SupportMessage[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .select('*')
        .eq('thread_id', threadId)
        .order('created_at', { ascending: true });
      if (error) return [];
      return (data || []).map(dbToMessage);
    } catch {
      return [];
    }
  },

  async send(msg: Omit<SupportMessage, 'id' | 'createdAt' | 'isRead'>): Promise<SupportMessage | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .insert({
          thread_id: msg.threadId,
          sender_id: msg.senderId,
          sender_name: msg.senderName,
          sender_role: msg.senderRole,
          message: msg.message,
          is_read: false,
        })
        .select()
        .single();
      if (error) return null;
      return data ? dbToMessage(data) : null;
    } catch {
      return null;
    }
  },

  async markThreadRead(threadId: string, readerRole: 'student' | 'lecturer' | 'admin'): Promise<void> {
    const supabase = createClient();
    try {
      // Mark messages not sent by this role as read
      await supabase
        .from('support_messages')
        .update({ is_read: true })
        .eq('thread_id', threadId)
        .neq('sender_role', readerRole)
        .eq('is_read', false);
    } catch { /* ignore */ }
  },

  async getAllThreads(): Promise<SupportThread[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('support_messages')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) return [];
      const messages = (data || []).map(dbToMessage);
      // Group by threadId
      const threadMap = new Map<string, SupportThread>();
      for (const msg of messages) {
        if (!threadMap.has(msg.threadId)) {
          const studentMsg = messages.find(m => m.threadId === msg.threadId && m.senderRole === 'student');
          threadMap.set(msg.threadId, {
            threadId: msg.threadId,
            studentId: studentMsg?.senderId || msg.threadId,
            studentName: studentMsg?.senderName || 'Student',
            lastMessage: msg.message,
            lastMessageAt: msg.createdAt,
            unreadCount: 0,
          });
        }
        const thread = threadMap.get(msg.threadId)!;
        if (!msg.isRead && msg.senderRole === 'student') {
          thread.unreadCount += 1;
        }
      }
      return Array.from(threadMap.values()).sort(
        (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
      );
    } catch {
      return [];
    }
  },

  subscribeToThread(threadId: string, onMessage: (msg: SupportMessage) => void) {
    const supabase = createClient();
    const channel = supabase
      .channel(`support:${threadId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `thread_id=eq.${threadId}` },
        (payload) => { if (payload.new) onMessage(dbToMessage(payload.new)); }
      )
      .subscribe();
    return channel;
  },

  subscribeToAllThreads(onUpdate: () => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('support_all_threads')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages' }, () => {
        onUpdate();
      })
      .subscribe();
    return channel;
  },
};
